import { defineMessages, IntlShape } from 'react-intl';
import { UseMutationResult } from '@tanstack/react-query';
import { isObject, isString } from 'lodash';

import { HoldActionErrorDto } from '@app/hooks';

const errorReasonMessages = defineMessages({
  UNKNOWN_PICKUP_LOCATION_CODE: {
    id: 'placeHoldModal.error.unknownPickupLocationCode',
    defaultMessage:
      'There has been an error in selecting the pickup location. Please try again or select a different pickup location. If this error persists, please notify your library.',
  },
  PICKUP_LOCATION_NOT_MAPPED_TO_AGENCY: {
    id: 'placeHoldModal.error.pickupLocationNotMappedToAgency',
    defaultMessage:
      'The selected pickup location is not associated with a valid agency. Please try again or select a different pickup location. If this error persists, please notify your library.',
  },
  DUPLICATE_REQUEST_ATTEMPT: {
    id: 'placeHoldModal.error.duplicateRequestAttempt',
    defaultMessage:
      'It appears you already have an existing request for this resource. Please modify your request. If this message is in error, please notify your library.',
  },
  PATRON_NOT_FOUND: {
    id: 'placeHoldModal.error.patronNotFound',
    defaultMessage:
      'A borrower account could not be found using the information provided.',
  },
  INVALID_PATRON_BARCODE: {
    id: 'placeHoldModal.error.invalidPatronBarcode',
    defaultMessage: 'The barcode provided is invalid.',
  },
  PATRON_INELIGIBLE: {
    id: 'placeHoldModal.error.patronIneligible',
    defaultMessage: 'This borrower account is ineligible for requesting.',
  },
  PATRON_BLOCKED: {
    id: 'placeHoldModal.error.patronBlocked',
    defaultMessage:
      'This borrower account is unable to place requests at this time. Please contact your library for additional information.',
  },
  PATRON_INACTIVE: {
    id: 'placeHoldModal.error.patronInactive',
    defaultMessage:
      'This borrower account is inactive and unable to borrow. Please contact your library for additional information.',
  },
  PATRON_AGENCY_NOT_PARTICIPATING_IN_BORROWING: {
    id: 'placeHoldModal.error.patronAgencyNotParticipatingInBorrowing',
    defaultMessage:
      'Your library does not participate in the borrowing program. Please contact your library for additional information.',
  },
  PATRON_TYPE_NOT_MAPPED: {
    id: 'placeHoldModal.error.patronTypeNotMapped',
    defaultMessage:
      'Your patron type is not recognized. Please contact your library for additional information.',
  },
  LOCAL_PATRON_TYPE_IS_NON_NUMERIC: {
    id: 'placeHoldModal.error.localPatronTypeIsNonNumeric',
    defaultMessage:
      'This borrower account has an invalid patron type. Please contact your library for assistance.',
  },
  PATRON_NOT_ASSOCIATED_WITH_AGENCY: {
    id: 'placeHoldModal.error.patronNotAssociatedWithAgency',
    defaultMessage:
      'We are unable to determine your home library using the information provided. Please contact your library for assistance.',
  },
  UNKNOWN_BORROWING_HOST_LMS: {
    id: 'placeHoldModal.error.unknownBorrowingHostLms',
    defaultMessage:
      'There has been an error validating your borrower information. Please wait a few minutes and try again. If this problem persists, please contact your library for assistance.',
  },
  NO_ITEM_SELECTABLE_FOR_REQUEST: {
    id: 'placeHoldModal.error.noItemSelectableForRequest',
    defaultMessage: "We're sorry, we were unable to locate an available item.",
  },
  CLUSTER_RECORD_NOT_FOUND: {
    id: 'placeHoldModal.error.clusterRecordNotFound',
    defaultMessage:
      "We're sorry, we were unable to find the title being requested. Please re-enter your search. If you believe you have reached this message in error, please reach out to your library for assistance.",
  },
});

export const getErrorReason = (
  holdMutationResult:
    | UseMutationResult<unknown, HoldActionErrorDto>
    | null
    | undefined,
  $t: IntlShape['$t']
): string | undefined => {
  if (holdMutationResult?.isError && holdMutationResult.error?.response) {
    let errorMessage = '';

    if (isObject(holdMutationResult.error.response.data)) {
      errorMessage = holdMutationResult.error.response.data.errorMessage;
    }

    if (isString(holdMutationResult.error.response.data)) {
      errorMessage = holdMutationResult.error.response.data;
    }

    return parseErrorCode(errorMessage, $t);
  }

  return undefined;
};

const parseErrorCode = (errorMessage: string, $t: IntlShape['$t']): string => {
  // Error code is contained in parentheses
  // e.g. "Cluster record \"123\" cannot be found(CLUSTER_RECORD_NOT_FOUND)."
  const errorCodeRegex = /\((?<errorCode>[A-Z_]+)\)/;
  const errorCode = errorMessage.match(errorCodeRegex)?.groups?.errorCode ?? '';

  return errorReasonMessages[errorCode]
    ? $t(errorReasonMessages[errorCode])
    : errorMessage;
};
